import React, {FunctionComponent, ReactNode} from "react";
import clsx from "clsx";
import {MCHeightLg, MCHeightSm} from "../common/questionConstants";
import {createUseStyles} from "react-jss";
import {Theme} from "engage-client/theme";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import UserProvidedText from "engage-client/components/UserProvidedText";

type MCOptionProps = {
	animationOffset?: string
	color: string
	label: ReactNode
	labelWidth?: number|string
	name?: string
	disabled?: boolean
	checked?: boolean
	className?: string
	onChange?: () => void
	children: string
}

const Animation = ".25s ease-in-out";

const useOptionStyles = createUseStyles<Theme>((theme) => ({
	mcButton: {
		display: "flex",
		height: MCHeightSm,
		width: "100%",
		[theme.breakpoints.up("lg")]: {
			height: MCHeightLg,
		},
		userSelect: "none"
	},
	// From https://webaim.org/techniques/css/invisiblecontent/
	input: {
		clip: "rect(1px, 1px, 1px, 1px)",
		clipPath: "inset(50%)",
		height: 1,
		width: 1,
		margin: -1,
		overflow: "hidden",
		padding: 0,
		position: "absolute"
	},
	option: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: (props) => props.color,
		boxSizing: "border-box",
		padding: "2px 6px",
		color: "#fff",
		width: (props) => props.labelWidth,
		[theme.breakpoints.up("xxl")] : {
			width: "3vw"
		}
	},
	content: {
		position: "relative",
		flex: 1
	},
	text: {
		position: "absolute",
		display: "inline-flex",
		alignItems: "center",
		padding: "0 6px",
		backgroundColor: "rgba(239,237,243, 1)",
	},
	mainText: {
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
	},
	overlayText: {
		transition: `clip-path ${Animation}, -webkit-clip-path ${Animation}`,
		color: "#fff",
		left: -1,
		right: -1,
		top: 0,
		bottom: 0,
		clipPath: "inset(0 100% 0 0)",
		"-webkit-clip-path": "inset(0 100% 0 0)",
		overflow: "hidden",
		background: (props) => props.color,
		"input:checked ~ * &, .checked &": {
			clipPath: ({animationOffset}) => `inset(0 ${animationOffset} 0 0)`,
			"-webkit-clip-path": ({animationOffset}) => `inset(0 ${animationOffset} 0 0)`
		}
	}
}));

const MultipleChoiceButton:FunctionComponent<MCOptionProps> = ({
							    animationOffset = "0",
								color,
								label,
								labelWidth,
								name="multipleChoiceOption",
								className,
								disabled = false,
								checked = false,
								onChange,
								children}: MCOptionProps) => {
	const classes = useOptionStyles({color, labelWidth, animationOffset});
	return <label className={clsx(classes.mcButton, className)} style={{cursor: disabled ? "default": "pointer"}}>
		<input className={classes.input} checked={checked} name={name} type="radio" disabled={disabled} onChange={onChange} />
		<DisplayText component="span" variant={TextVariant.s22reg} className={classes.option}>{label}</DisplayText>
		<div className={classes.content}>
			<UserProvidedText className={clsx(classes.text, classes.mainText)} color="black" maxFont={[38,32,22,20,16]}>{children}</UserProvidedText>
			<UserProvidedText className={clsx(classes.text, classes.overlayText)} maxFont={[38,32,22,20,16]} >{children}</UserProvidedText>
		</div>
	</label>;
};

export default MultipleChoiceButton;